// The studio sweater screen (components/SweaterScreen.jsx): one row per hole
// with its size, where it is and how we fix it (reknit or machine darn), the
// other sweater repairs and washes, and Standard or Premium. No photos: the
// sweater is on the counter. It replaces the plan, service and repair-detail
// screens for a sweater; every other garment keeps the old path.
//
// The online version is fxryshop/src/utils/sweaterScreen.js. This one adds
// the method per hole and three ways to charge (Calley, 27 Sep 2026):
//   - per hole, each at its size's price, however many holes there are;
//   - restoration: one price for the whole sweater, typed in from the
//     off-menu quote tool (Prices tab), which staff round as they see fit;
//   - price to confirm later: when whoever estimates that method isn't in,
//     charge one hole now and invoice the rest with Add extra work.
// Plus custom work (3 Oct 2026): a repair or alteration typed in with its
// price, for work done by time, an odd job, or a sweater alteration.

import {
  TASK_FOR_KIND,
  serviceKind,
  shortServiceName,
} from "./serviceOptions.js";

export const isSweater = (item) =>
  String(item?.name ?? item ?? "")
    .trim()
    .toUpperCase() === "SWEATER";

export const HOLE_PLACES = [
  "Front, top",
  "Front, middle",
  "Front, bottom",
  "Back",
  "Sleeve",
  "Elbow",
  "Cuff",
  "Neck or collar",
  "Shoulder",
  "Underarm or side",
  "Hem",
  "Pocket",
];

/** The last choice opens a text box for a place the list doesn't name. */
export const SOMEWHERE_ELSE = "Somewhere else";

export const holeLocation = (hole) =>
  hole?.place === SOMEWHERE_ELSE
    ? String(hole?.other ?? "").trim()
    : String(hole?.place ?? "").trim();

/** How a hole is fixed. Saved as the repair's description, so the order page
 *  reads "Elbow — Machine darn" where the fixers already look. No default:
 *  staff pick one for every hole, so a forgotten click can't charge or
 *  route the wrong method (Calley, 18 Oct 2026). */
export const REKNIT = "Reknit";
export const MACHINE_DARN = "Machine darn";
export const METHODS = [REKNIT, MACHINE_DARN];

/** Shown under a hole once Reknit is picked: what to tell the customer at
 *  the counter. */
export const REKNIT_REMINDER =
  "Remind the customer: we do our best to match the yarn and colour, but it could be off.";

/** The studio takes sweaters with many holes (Calley: "maybe we want to
 *  charge 15 holes"), so the cap is only there to stop a runaway list. */
export const MAX_HOLES = 40;
export const MAX_COUNT = 12;

/** Longest note for the fixers, the same as the online screen. */
export const MAX_NOTE = 300;

/** From this many holes the screen asks how to charge; below it, per hole
 *  unless staff switch. */
export const RESTORATION_FROM = 6;

export const PER_HOLE = "per-hole";
export const RESTORATION = "restoration";
export const CONFIRM_LATER = "confirm-later";

export const RESTORATION_NAME = "SWEATER - RESTORATION";

/** Custom work: a repair or alteration that isn't on the list, typed in
 *  with its price, like the "Not on the list? Write it in" box on every
 *  other garment. */
export const CUSTOM_KINDS = [
  { kind: "repair", label: "Repair" },
  { kind: "alteration", label: "Alteration" },
];

export const MAX_CUSTOM = 10;

export const newCustom = () => ({ kind: "repair", what: "", price: "" });

/** The custom rows ready to charge, with a number price. A row left
 *  completely empty is skipped; a half-filled one stops Continue. */
export const chosenCustoms = (customs) =>
  (customs || [])
    .map((custom) => ({
      kind: custom?.kind === "alteration" ? "alteration" : "repair",
      what: String(custom?.what ?? "").trim(),
      price: restorationPriceOf(custom?.price),
    }))
    .filter((custom) => custom.what || custom.price);

export const customsTotal = (customs) =>
  chosenCustoms(customs).reduce((sum, custom) => sum + custom.price, 0);

export const newHole = () => ({
  size: 0,
  place: "",
  other: "",
  method: "",
});

export const isCountable = (service) =>
  /BUTTON|SNAP|HOOK/i.test(service?.name || "");

export const expandCounts = (services, counts) =>
  (Array.isArray(services) ? services : []).flatMap((service) =>
    Array.from(
      { length: Math.max(0, counts?.[service._id] || 0) },
      () => service,
    ),
  );

const HOLE = /FIX ONE HOLE/i;
const ADDITIONAL = /ADDITIONAL/i;

export const offeredOn = (service, plan) =>
  plan === "Premium"
    ? Boolean(service?.is_premium)
    : Boolean(service?.is_standard);

export const priceOn = (service, plan) => {
  const price = parseFloat(
    plan === "Premium" ? service?.premium_price : service?.standard_price,
  );
  return Number.isFinite(price) ? price : 0;
};

/** The hole sizes, smallest first, and everything else a sweater can have
 *  in the studio: elbow patches, depilling, buttons and the washes. */
export const splitSweaterServices = (services) => {
  const list = (Array.isArray(services) ? services : []).filter(
    (service) => serviceKind(service) !== "alteration",
  );
  const holes = list
    .filter(
      (s) =>
        serviceKind(s) === "repair" &&
        HOLE.test(s?.name || "") &&
        !ADDITIONAL.test(s?.name || ""),
    )
    .sort(
      (a, b) =>
        (parseFloat(a?.standard_price) || 0) -
        (parseFloat(b?.standard_price) || 0),
    );
  const extras = list.filter((s) => !HOLE.test(s?.name || ""));
  return { holes, extras };
};

export const holeSizeLabel = (service) => {
  const short = shortServiceName(service?.name, "SWEATER");
  const match = short.match(/UP TO\s+(.+)$/i);
  return match ? `Up to ${match[1].trim()}` : short;
};

export const extraLabel = (service) => {
  const short = shortServiceName(service?.name, "SWEATER").toLowerCase();
  return short.charAt(0).toUpperCase() + short.slice(1);
};

/** Who gives the time estimate: Beck or Calley for machine darning, Elsa or
 *  Natalie for reknitting (Calley, 27 Sep 2026). A rare mixed sweater needs
 *  both. */
export const whoEstimates = (holes) => {
  const darn = (holes || []).some((hole) => hole?.method === MACHINE_DARN);
  const reknit = (holes || []).some((hole) => hole?.method !== MACHINE_DARN);
  if (darn && reknit) {
    return "Beck or Calley (darning) and Elsa or Natalie (reknitting)";
  }
  return darn ? "Beck or Calley" : "Elsa or Natalie";
};

/** "Reknit" when every hole is reknit, "Machine darn 4, reknit 2" when mixed. */
export const methodSummary = (holes) => {
  const darn = (holes || []).filter((h) => h?.method === MACHINE_DARN).length;
  const reknit = (holes || []).length - darn;
  if (!darn) return REKNIT;
  if (!reknit) return MACHINE_DARN;
  return `Machine darn ${darn}, reknit ${reknit}`;
};

/** Each hole as "Elbow (machine darn)", joined, for a restoration line or a
 *  confirm-later note. */
export const holeList = (holes) =>
  (holes || [])
    .map((hole) => `${hole.location} (${String(hole.method).toLowerCase()})`)
    .join("; ");

export const restorationPriceOf = (typed) => {
  const price = parseFloat(typed);
  return Number.isFinite(price) && price > 0
    ? Math.round(price * 100) / 100
    : 0;
};

/** Every sweater hole costs its size's price, darned or reknit, however
 *  many there are (Calley, 31 Oct 2026). There is no extra-hole price any
 *  more: the 3 Oct 2026 rule gave a darned hole $20 once there were more
 *  than three, and it was retired because it fired on three sweaters in
 *  nine months, worth about $7 a month, while costing a threshold the
 *  counter app and the server both had to keep in step.
 *
 *  Orders taken while that rule was live carry `darn_extra_hole` on the
 *  line, and Price history still reads it (helper/pricingReport.js), so
 *  they are not read back as undercharged. */
export const holePrices = (holes, plan) =>
  (holes || []).map((hole) => priceOn(hole?.service, plan));

/** What the sweater costs now, before extras, for the way it is charged. */
export const holesTotal = ({ holes, plan, charge, restorationPrice }) => {
  if (!holes?.length) return 0;
  if (charge === RESTORATION) return restorationPriceOf(restorationPrice);
  if (charge === CONFIRM_LATER) return priceOn(holes[0].service, plan);
  return holePrices(holes, plan).reduce((sum, price) => sum + price, 0);
};

/** One entry per hole for its line: its own service at its own price, and
 *  the method as the description, so the fixer sees every hole. */
export const darnedHoleLines = (holes) =>
  (holes || []).map((hole) => ({
    service: hole.service,
    location: hole.location,
    description: hole.method,
  }));

/** What stops Continue, in the words the screen shows. Empty when ready. */
export const missingForContinue = ({
  holes,
  extras,
  charge,
  restorationPrice,
  customs = [],
}) => {
  const missing = [];
  const custom = chosenCustoms(customs);
  if (!holes.length && !extras.length && !custom.length) {
    missing.push("add a hole or a repair");
  }
  if (holes.some((hole) => !hole.location)) {
    missing.push("say where each hole is");
  }
  if (holes.some((hole) => !METHODS.includes(hole.method))) {
    missing.push("pick reknit or machine darn for each hole");
  }
  if (
    holes.length &&
    charge === RESTORATION &&
    !restorationPriceOf(restorationPrice)
  ) {
    missing.push("type the restoration price");
  }
  if (custom.some((c) => !c.what)) missing.push("say what the custom work is");
  if (custom.some((c) => !c.price)) missing.push("type the custom work price");
  return missing;
};

/**
 * The basket after this screen: the garment gets its plan, and its lines are
 * replaced. Lines are shaped like the ones the old screens build, so the
 * basket, checkout and order creation read them unchanged; the order saves
 * each line whole as the job's service_details.
 *
 * - Per hole: one line per hole, location and method in repair_details. A
 *   machine-darned hole after the third carries its $20 as the line's price
 *   and says so in its description, so the fixer sees every hole.
 * - Restoration: one off-menu line (is_custom, like the Custom repair box)
 *   at the typed price, every hole listed in its repair details.
 * - Confirm later: the first hole's line, charged now, with every hole and
 *   who to ask in its notes to tailors.
 * - Custom work: one off-menu line each (is_custom), named as typed, at the
 *   typed price on both plans, after the holes and extras.
 * - Restoration and custom lines carry their own override_reason, so the
 *   studio checkout doesn't ask "Reason for custom price" again.
 */
export const sweaterBasket = (
  cart,
  {
    garmentId,
    plan,
    holes,
    extras,
    charge,
    restorationPrice,
    notes = "",
    customs = [],
  },
) => {
  const itemsData = (cart?.itemsData || []).map((item) =>
    item?.id === garmentId ? { ...item, pricing_plan: plan } : item,
  );
  const garment = itemsData.find((item) => item?.id === garmentId);
  const line = (service, repair, more = {}) => ({
    ...service,
    selectedOption: service.name,
    selectedService: TASK_FOR_KIND[serviceKind(service)] || "Repairing",
    item_details: garment,
    repair_details: {
      location: repair?.location || "",
      description: repair?.description || "",
    },
    intake_screen: "sweater",
    ...more,
  });

  let holeLines = [];
  if (holes.length && charge === RESTORATION) {
    const price = restorationPriceOf(restorationPrice);
    holeLines = [
      line(
        {
          id: `restoration-${garmentId}`,
          name: RESTORATION_NAME,
          standard_price: price,
          premium_price: price,
          is_standard: true,
          is_premium: true,
          service_type: false,
          service_kind: "repair",
          is_custom: true,
          override_reason: `Sweater restoration, ${holes.length} holes`,
        },
        {
          location: `${holes.length} holes`,
          description: `${methodSummary(holes)}. ${holeList(holes)}`,
        },
      ),
    ];
  } else if (holes.length && charge === CONFIRM_LATER) {
    const first = holes[0];
    holeLines = [
      line(
        first.service,
        { location: first.location, description: first.method },
        {
          item_notes:
            `Price to confirm: ${holes.length} holes. ${holeList(holes)}. ` +
            `Charged one hole at check-in. Get a time estimate from ` +
            `${whoEstimates(holes)}, then invoice the rest with Add extra work.`,
        },
      ),
    ];
  } else {
    holeLines = darnedHoleLines(holes).map(
      ({ service, location, description }) =>
        line(service, { location, description }),
    );
  }

  const kept = (cart?.services || []).filter(
    (service) => service?.item_details?.id !== garmentId,
  );
  const customLines = chosenCustoms(customs).map((custom, index) =>
    line(
      {
        id: `custom-${garmentId}-${index}`,
        name: custom.what,
        standard_price: custom.price,
        premium_price: custom.price,
        is_standard: true,
        is_premium: true,
        service_type: custom.kind === "alteration",
        service_kind: custom.kind,
        is_custom: true,
        // What staff typed is the reason for the price, so checkout
        // doesn't ask for one again.
        override_reason: `Sweater custom ${custom.kind}: ${custom.what}`,
      },
      null,
    ),
  );
  const lines = [
    ...holeLines,
    ...extras.map((service) => line(service, null)),
    ...customLines,
  ];
  // The note goes on the first line only, where the other screens put
  // item_notes ("Notes to tailors"), so it shows once; BEFORE the
  // price-to-confirm note when there is one. The sticker cuts notes at 140
  // characters and the price note alone runs past that, so a typed note
  // after it never printed (2026-09-30).
  const note = String(notes || "").trim();
  if (note && lines.length) {
    lines[0] = {
      ...lines[0],
      item_notes: [note, lines[0].item_notes].filter(Boolean).join(" · "),
    };
  }
  return {
    itemsData,
    services: [...kept, ...lines],
  };
};
